import { useRef } from 'react'

/**
 * 6-digit code input with auto-advance
 */
const CodeInput = ({ value, onChange, disabled = false }) => {
  const inputRefs = useRef([])

  const handleInput = (index, inputValue) => {
    // Only allow single digit
    const digit = inputValue.slice(-1)
    if (digit && !/\d/.test(digit)) return

    const newCode = [...value]
    newCode[index] = digit
    onChange(newCode)

    // Auto-focus next field
    if (digit && index < 5) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus()
      }, 0)
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newCode = [...value]
      newCode[index] = ''
      onChange(newCode)

      // Move to previous field if current is empty
      if (!newCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')

    // Extract only digits from pasted text
    const digits = pastedText.replace(/\D/g, '').split('')

    // Fill up to 6 digits
    const newCode = [...value]
    for (let i = 0; i < Math.min(digits.length, 6); i++) {
      newCode[i] = digits[i]
    }

    onChange(newCode)

    // Focus the next empty field or the last field if all are filled
    const nextEmptyIndex = newCode.findIndex(d => !d)
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

    setTimeout(() => {
      inputRefs.current[focusIndex]?.focus()
    }, 0)
  }

  return (
    <div className="flex gap-3 justify-center">
      {value.map((digit, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          maxLength="1"
          value={digit}
          onChange={(e) => handleInput(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={`
            w-12 h-14 text-center text-2xl font-semibold rounded-lg
            border-2 transition-all
            ${digit
              ? 'border-green-500 dark:border-green-400'
              : 'border-gray-300 dark:border-gray-600'
            }
            bg-white dark:bg-gray-800
            text-gray-900 dark:text-gray-100
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
          `}
        />
      ))}
    </div>
  )
}

export default CodeInput
